import { useEffect, useState } from 'react'
import { getPackQuestionCount } from '../lib/offlineDb'

// Module-level over sessionStorage on purpose: a full page reload reloads the
// module so refresh always re-shows the "Bem-vindo" welcome. Refresh is the
// student's signal that they want it to work. SPA navigation keeps the module
// in memory, so post-cold-boot loads auto-switch to the shorter "returning"
// copy — no re-welcomes on every route change, but the warm loader (logo +
// rotating messages + offline escalation) still runs. That way a slow API
// call after the initial boot no longer strands the student on a blank
// screen for 10s waiting for the backend.
let hasBootedOnce = false

interface BootWelcomeProps {
  variant?: 'welcome' | 'returning'
}

// Rotated so a longer wait doesn't feel stuck on one line. Arc goes from
// warm-greeting to youth register on purpose: the longer the wait, the more
// the copy leans into slang ("txunar", "a cena") so it reads as energy
// instead of apology.
const WELCOME_LINES = [
  'Bem-vindo. Estamos a preparar tudo, só uns segundos.',
  'Estamos a txunar as tuas matérias.',
  'Quase lá, relaxa um bocadinho.',
  'Aprender é a cena, vamos a isso!',
]

const RETURNING_LINES = [
  'A voltar ao teu ponto…',
  'Só uns segundos.',
  'Quase lá.',
]

const LINE_ROTATION_MS = 4_000
const HINT_AT_MS = 10_000
// Switches the copy only. This used to render a dead-end "não consegui
// contactar o servidor" card whose single action was a reload, which is the
// wrong answer twice over: it fired at 20s while FETCH_TIMEOUT_MS in api.ts
// is 30s (so it gave up on requests that still had 10s to succeed), and a
// reload on a dead network just repeats the failure. Offline is a feature
// here, not an error — useAuth keeps the cached student on OfflineError and
// the app serves questions from IndexedDB, so boot must never block that.
const OFFLINE_COPY_AT_MS = 20_000

// Only claim saved questions when there actually are some. A first-time
// student offline has an empty pack, and promising them cached work they
// don't have reads as a bug the moment the app lands on the login screen.
const OFFLINE_WITH_PACK = 'Sem net, a usar as tuas questões guardadas. Vamos a isso!'
const OFFLINE_NO_PACK = 'Sem net por agora. A tentar ligar…'

export function BootWelcome({ variant = 'welcome' }: BootWelcomeProps) {
  // Snapshot at mount so hooks always run in the same order across renders.
  // After the first cold boot in this tab, auto-shift callers using the
  // default variant to 'returning' so SPA nav gets the shorter copy without
  // needing every caller to pass the prop.
  const [alreadyBooted] = useState(hasBootedOnce)
  const effectiveVariant = alreadyBooted && variant === 'welcome' ? 'returning' : variant
  const [idx, setIdx] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [nowOffline, setNowOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false,
  )
  const [hasPack, setHasPack] = useState(false)

  useEffect(() => {
    hasBootedOnce = true
  }, [])

  // Read once at mount so the offline copy can tell the truth about whether
  // there is anything cached to fall back on. Failure means "assume nothing",
  // which only ever downgrades the message.
  useEffect(() => {
    let alive = true
    getPackQuestionCount()
      .then((n) => { if (alive) setHasPack(n > 0) })
      .catch(() => { /* no pack, keep the neutral copy */ })
    return () => { alive = false }
  }, [])

  const lines = effectiveVariant === 'returning' ? RETURNING_LINES : WELCOME_LINES

  useEffect(() => {
    const start = Date.now()
    const tick = window.setInterval(() => {
      const t = Date.now() - start
      setElapsed(t)
      setIdx(Math.min(lines.length - 1, Math.floor(t / LINE_ROTATION_MS)))
    }, 1000)
    const onOffline = () => setNowOffline(true)
    // Recovering matters now that offline is a live copy state rather than a
    // terminal card: a student who walks back into signal should stop being
    // told there is no connection.
    const onOnline = () => setNowOffline(false)
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    return () => {
      window.clearInterval(tick)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
    }
  }, [lines])

  // Offline is a copy change, never an exit. The student stays on the loader
  // until useAuth settles, and it always settles: cached student on
  // OfflineError, or null, which routes to LoginPage. Blocking here would
  // hide a working offline app behind a reload button.
  const isOffline = nowOffline || elapsed >= OFFLINE_COPY_AT_MS
  const message = isOffline
    ? (hasPack ? OFFLINE_WITH_PACK : OFFLINE_NO_PACK)
    : lines[idx]

  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 gap-4"
    >
      {/* Same SVG + pulse animation as index.html so the React mount is
          visually invisible. Bookend mapping: wordmark on the first and
          last lines (identity moment), T-mark on the middle lines where
          the copy is punchy and the icon carries the energy. */}
      {logoFor(idx, lines.length)}
      <div className="text-center max-w-sm">
        <p className="text-sm text-gray-700 leading-relaxed">
          {message}
        </p>
        {/* Redundant once the copy already says there is no connection. */}
        {!isOffline && elapsed >= HINT_AT_MS && (
          <p className="text-xs text-gray-400 mt-3">
            A demorar? Verifica a tua ligação.
          </p>
        )}
      </div>
    </div>
  )
}

// Wordmark on first + last line, T-mark on the middle. Works for both the
// 4-line welcome arc and the 3-line returning arc: index 0 and (length-1)
// bookend the sequence, everything in between is T-mark.
function logoFor(idx: number, total: number): JSX.Element {
  const isBookend = idx === 0 || idx === total - 1
  return isBookend ? <WordmarkLogo /> : <TMarkLogo />
}

const PULSE_STYLE = {
  animation: 'bootPulse 1.6s ease-in-out infinite',
  transformOrigin: 'center' as const,
}

function TMarkLogo() {
  return (
    <svg
      viewBox="0 0 227 291"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Tutor"
      style={{ width: 64, height: 'auto', ...PULSE_STYLE }}
    >
      <g transform="matrix(1,0,0,1,-16864.9,-413.823)">
        <g transform="matrix(-1,0,0,1,33951.8,22.1948)">
          <path
            d="M16978.7,605.123C16976.9,605.233 16975.1,605.313 16973.2,605.363C16950.9,605.665 16931.4,601.061 16914.6,591.552C16897.9,582.044 16884.7,569.063 16875.2,552.612C16865.7,536.16 16860.7,517.973 16860.1,498.05C16859.7,477.523 16864.4,459.335 16874.1,443.487C16883.7,427.639 16897.2,415.036 16914.4,405.679C16931.6,396.321 16951.2,391.642 16973.2,391.642C16995,391.34 17014.2,395.868 17031,405.226C17047.7,414.584 17061.1,427.262 17071.1,443.261C17081,459.26 17086.3,477.523 17086.9,498.05C17087.2,517.671 17082.5,535.556 17072.9,551.706C17065,564.845 17054.7,575.787 17041.8,584.53L17019.6,564.666L17014.4,561.151L17008.5,559.215L17002.4,558.921L16996.2,560.332L16990.7,563.363L16986.3,567.692L16983.2,573.045L16981.6,579.144L16978.7,605.123ZM16974.6,539.254C16982.8,539.254 16989.9,536.085 16995.9,529.745C17001.9,523.406 17004.9,516.011 17004.9,507.558C17004.9,499.106 17001.9,491.786 16995.9,485.598C16989.9,479.409 16982.8,476.315 16974.6,476.315C16966.5,476.315 16959.4,479.409 16953.3,485.598C16947.3,491.786 16944.3,499.106 16944.3,507.558C16944.3,516.011 16947.3,523.406 16953.3,529.745C16959.4,536.085 16966.5,539.254 16974.6,539.254Z"
            fill="#1946b9"
          />
        </g>
        <g transform="matrix(-1,0,0,1,33951.8,22.1948)">
          <path
            d="M16995.4,580.692C16995.8,577.343 16998,574.484 17001.2,573.284C17004.3,572.083 17007.9,572.742 17010.4,574.99L17062.6,621.765C17065.6,624.467 17066.8,628.678 17065.6,632.549C17064.4,636.419 17061.1,639.242 17057,639.778L17039.4,642.132L17055.6,666.388C17057.1,668.632 17057.4,671.476 17056.4,673.97C17055.3,676.465 17053.1,678.282 17050.5,678.816L17034.4,682.065C17028.7,683.212 17023.1,679.76 17021.5,674.163L17014.7,649.216L17001.6,659.203C16998.8,661.343 16995,661.604 16991.9,659.865C16988.8,658.126 16987.1,654.724 16987.5,651.219L16995.4,580.692Z"
            fill="#1946b9"
          />
        </g>
      </g>
    </svg>
  )
}

function WordmarkLogo() {
  return (
    <svg
      viewBox="0 0 1185 394"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Tutor"
      style={{ width: 160, height: 'auto', ...PULSE_STYLE }}
    >
      <g transform="matrix(1,0,0,1,-16141.5,-321.521)">
        <g>
          <g transform="matrix(0.913899,-0.405941,0.405941,0.913899,1185.92,6687.38)">
            <path d="M16303,698.479C16292.4,698.479 16281.7,696.366 16270.8,692.14C16260,687.914 16250.2,681.726 16241.6,673.576C16233,665.425 16226.8,655.615 16222.8,644.144C16220.1,636.597 16218.1,627.239 16216.9,616.07C16215.7,604.901 16214.9,592.977 16214.5,580.299C16214,567.621 16213.8,555.244 16213.8,543.17C16202,542.868 16190.8,541.811 16180,540C16169.3,538.189 16160.6,534.793 16153.8,529.812C16147,524.831 16143.6,517.511 16143.6,507.851C16143.6,496.079 16146.8,487.249 16153.1,481.363C16159.4,475.476 16168,471.552 16178.7,469.59C16189.4,467.628 16201.1,466.496 16213.8,466.194C16214.1,456.836 16214.9,447.252 16216.3,437.441C16217.6,427.631 16220,418.499 16223.3,410.047C16226.6,401.595 16231.3,394.727 16237.3,389.445C16243.4,384.162 16251.2,381.521 16260.9,381.521C16273.8,381.521 16283.3,385.596 16289.2,393.746C16295,401.896 16298.7,412.462 16300.3,425.442C16301.8,438.422 16302.4,452.006 16302.1,466.194C16317.8,465.892 16331.6,466.496 16343.7,468.005C16355.8,469.514 16365.3,472.835 16372.3,477.967C16379.2,483.098 16382.7,491.249 16382.7,502.418C16382.7,513.889 16378.7,522.492 16370.9,528.227C16363,533.963 16352.8,537.811 16340.1,539.774C16327.4,541.736 16314,542.868 16299.8,543.17C16299.8,548.905 16299.7,554.867 16299.6,561.055C16299.4,567.243 16299.4,573.507 16299.4,579.846C16299.4,597.053 16300.6,609.278 16303,616.523C16304.8,621.957 16307.3,625.428 16310.4,626.937C16313.6,628.447 16317.6,629.277 16322.4,629.428C16327.3,629.579 16333.2,630.56 16340.1,632.371C16347,633.88 16352.5,637.578 16356.4,643.464C16360.3,649.351 16362.3,656.822 16362.3,665.878C16362.3,676.141 16356.9,684.141 16346.2,689.876C16335.5,695.612 16321.1,698.479 16303,698.479Z" fill="#1946b9" />
          </g>
          <g transform="matrix(0.96709,-0.254433,0.254433,0.96709,401.235,4172.07)">
            <path d="M16496.8,698.479C16474.4,698.479 16455,692.97 16438.6,681.952C16422.1,670.934 16409.5,655.539 16400.6,635.767C16391.6,615.995 16387.2,593.128 16387.2,567.168C16387.2,541.811 16391.2,521.888 16399.2,507.399C16407.2,492.909 16418.6,485.664 16433.4,485.664C16443.9,485.664 16452,487.626 16457.6,491.551C16463.2,495.475 16467,500.38 16468.9,506.267C16470.9,512.153 16471.9,518.417 16471.9,525.058C16471.9,533.51 16471.3,542.113 16470.3,550.867C16469.2,559.621 16468.7,568.979 16468.7,578.941C16468.7,593.128 16471.6,603.467 16477.5,609.957C16483.4,616.447 16489.8,619.693 16496.8,619.693C16504.3,619.693 16510.9,616.447 16516.5,609.957C16522.1,603.467 16524.8,593.128 16524.8,578.941C16524.8,568.979 16524.4,560.074 16523.5,552.226C16522.6,544.377 16522.1,535.472 16522.1,525.51C16522.1,514.945 16524.7,505.663 16529.8,497.663C16535,489.664 16545.2,485.664 16560.6,485.664C16575.4,485.664 16586.7,492.909 16594.6,507.399C16602.4,521.888 16606.3,541.811 16606.3,567.168C16606.3,593.128 16602.3,615.995 16594.1,635.767C16586,655.539 16573.8,670.934 16557.7,681.952C16541.5,692.97 16521.2,698.479 16496.8,698.479Z" fill="#1946b9" />
          </g>
          <g transform="matrix(1,0,0,1,0,-60)">
            <path d="M16771.6,698.479C16761.1,698.479 16750.3,696.366 16739.5,692.14C16728.6,687.914 16718.9,681.726 16710.3,673.576C16701.7,665.425 16695.4,655.615 16691.5,644.144C16688.8,636.597 16686.8,627.239 16685.6,616.07C16684.4,604.901 16683.5,592.977 16683.1,580.299C16682.6,567.621 16682.4,555.244 16682.4,543.17C16670.6,542.868 16659.4,541.811 16648.7,540C16638,538.189 16629.2,534.793 16622.4,529.812C16615.6,524.831 16612.2,517.511 16612.2,507.851C16612.2,496.079 16615.4,487.249 16621.7,481.363C16628.1,475.476 16636.6,471.552 16647.3,469.59C16658,467.628 16669.7,466.496 16682.4,466.194C16682.7,456.836 16683.5,447.252 16684.9,437.441C16686.3,427.631 16688.6,418.499 16691.9,410.047C16695.2,401.595 16699.9,394.727 16706,389.445C16712,384.162 16719.8,381.521 16729.5,381.521C16742.5,381.521 16751.9,385.596 16757.8,393.746C16763.7,401.896 16767.4,412.462 16768.9,425.442C16770.4,438.422 16771,452.006 16770.7,466.194C16786.4,465.892 16800.3,466.496 16812.4,468.005C16824.4,469.514 16834,472.835 16840.9,477.967C16847.8,483.098 16851.3,491.249 16851.3,502.418C16851.3,513.889 16847.4,522.492 16839.5,528.227C16831.7,533.963 16821.4,537.811 16808.7,539.774C16796.1,541.736 16782.6,542.868 16768.4,543.17C16768.4,548.905 16768.4,554.867 16768.2,561.055C16768.1,567.243 16768,573.507 16768,579.846C16768,597.053 16769.2,609.278 16771.6,616.523C16773.4,621.957 16775.9,625.428 16779.1,626.937C16782.3,628.447 16786.3,629.277 16791.1,629.428C16795.9,629.579 16801.8,630.56 16808.7,632.371C16815.7,633.88 16821.1,637.578 16825,643.464C16829,649.351 16830.9,656.822 16830.9,665.878C16830.9,676.141 16825.6,684.141 16814.9,689.876C16804.1,695.612 16789.7,698.479 16771.6,698.479Z" fill="#1946b9" />
          </g>
          <g transform="matrix(-1,0,0,1,33951.8,22.1948)">
            <path d="M16978.7,605.123C16976.9,605.233 16975.1,605.313 16973.2,605.363C16950.9,605.665 16931.4,601.061 16914.6,591.552C16897.9,582.044 16884.7,569.063 16875.2,552.612C16865.7,536.16 16860.7,517.973 16860.1,498.05C16859.7,477.523 16864.4,459.335 16874.1,443.487C16883.7,427.639 16897.2,415.036 16914.4,405.679C16931.6,396.321 16951.2,391.642 16973.2,391.642C16995,391.34 17014.2,395.868 17031,405.226C17047.7,414.584 17061.1,427.262 17071.1,443.261C17081,459.26 17086.3,477.523 17086.9,498.05C17087.2,517.671 17082.5,535.556 17072.9,551.706C17065,564.845 17054.7,575.787 17041.8,584.53L17019.6,564.666L17014.4,561.151L17008.5,559.215L17002.4,558.921L16996.2,560.332L16990.7,563.363L16986.3,567.692L16983.2,573.045L16981.6,579.144L16978.7,605.123ZM16974.6,539.254C16982.8,539.254 16989.9,536.085 16995.9,529.745C17001.9,523.406 17004.9,516.011 17004.9,507.558C17004.9,499.106 17001.9,491.786 16995.9,485.598C16989.9,479.409 16982.8,476.315 16974.6,476.315C16966.5,476.315 16959.4,479.409 16953.3,485.598C16947.3,491.786 16944.3,499.106 16944.3,507.558C16944.3,516.011 16947.3,523.406 16953.3,529.745C16959.4,536.085 16966.5,539.254 16974.6,539.254Z" fill="#1946b9" />
          </g>
          <g transform="matrix(0.917669,0.397345,-0.397345,0.917669,1664.31,-6766.5)">
            <path d="M17140.2,698.479C17129.9,698.479 17121.6,695.083 17115.1,688.291C17108.6,681.499 17103.7,672.368 17100.4,660.897C17097,649.426 17094.8,636.295 17093.6,621.504C17092.4,606.712 17091.7,591.468 17091.7,575.771C17091.7,565.206 17092,554.414 17092.4,543.396C17092.9,532.378 17094.4,522.19 17097,512.832C17099.5,503.474 17103.9,495.852 17110.1,489.966C17116.3,484.079 17125.1,481.136 17136.6,481.136C17144.7,481.136 17152.1,483.023 17158.8,486.796C17165.4,490.57 17169.6,496.229 17171.4,503.776C17178.4,497.437 17187.2,492.834 17197.9,489.966C17208.6,487.098 17219.7,485.664 17231.2,485.664C17240.9,485.664 17250.7,488.004 17260.6,492.683C17270.6,497.361 17279.1,503.776 17286,511.927C17292.9,520.077 17296.4,529.284 17296.4,539.547C17296.4,548.301 17293.2,556.603 17286.7,564.451C17280.2,572.3 17271.1,576.224 17259.3,576.224C17254.2,576.224 17249.9,575.545 17246.6,574.186C17243.3,572.828 17240.1,571.545 17237.1,570.337C17234.1,569.13 17230.5,568.526 17226.2,568.526C17215.1,568.526 17204.9,572.979 17195.7,581.884C17186.5,590.789 17181.9,602.788 17181.9,617.881C17181.9,625.73 17181.6,634.258 17181.2,643.464C17180.7,652.671 17179.4,661.501 17177.1,669.953C17174.8,678.405 17170.8,685.273 17165.1,690.555C17159.4,695.838 17151.1,698.479 17140.2,698.479Z" fill="#1946b9" />
          </g>
          <g transform="matrix(-1,0,0,1,33951.8,22.1948)">
            <path d="M16995.4,580.692C16995.8,577.343 16998,574.484 17001.2,573.284C17004.3,572.083 17007.9,572.742 17010.4,574.99L17062.6,621.765C17065.6,624.467 17066.8,628.678 17065.6,632.549C17064.4,636.419 17061.1,639.242 17057,639.778L17039.4,642.132L17055.6,666.388C17057.1,668.632 17057.4,671.476 17056.4,673.97C17055.3,676.465 17053.1,678.282 17050.5,678.816L17034.4,682.065C17028.7,683.212 17023.1,679.76 17021.5,674.163L17014.7,649.216L17001.6,659.203C16998.8,661.343 16995,661.604 16991.9,659.865C16988.8,658.126 16987.1,654.724 16987.5,651.219L16995.4,580.692Z" fill="#1946b9" />
          </g>
        </g>
      </g>
    </svg>
  )
}

export default BootWelcome
